import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { UnitDto, CreateUnitDto, UpdateUnitDto, UnitStatus } from '../../types';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';

interface UnitFormProps {
	unit?: UnitDto | null;
	onSubmit: (data: CreateUnitDto | UpdateUnitDto) => void;
	isLoading?: boolean;
	error?: string;
}

const unitSchema = yup.object({
	selectedClientId: yup.number().required('Client is required'),
	clientLocationId: yup.number().required('Location is required'),
	unitNumber: yup.string().required('Unit number is required').max(50, 'Unit number must be less than 50 characters'),
	unitType: yup.string().max(50, 'Unit type must be less than 50 characters').nullable(),
	squareFeet: yup.number().min(1).nullable(),
	bedrooms: yup.number().min(0).max(20).nullable(),
	bathrooms: yup.number().min(0).max(20).nullable(),
	floor: yup.number().min(0).nullable(),
	status: yup
		.mixed<number>()
		.required('Status is required')
		.transform((value) => {
			if (typeof value === 'number' && !isNaN(value)) {
				return value;
			}
			if (typeof value === 'string') {
				const parsed = parseInt(value, 10);
				if (!isNaN(parsed)) {
					return parsed;
				}
			}
			return UnitStatus.Vacant;
		})
		.test('is-number', 'Status must be a number', (value) => typeof value === 'number' && !isNaN(value)),
	occupiedSince: yup.string().nullable(),
	vacantSince: yup.string().nullable(),
	monthlyRent: yup.number().min(0).nullable(),
	securityDeposit: yup.number().min(0).nullable(),
	accessNotes: yup.string().max(500, 'Access notes must be less than 500 characters').nullable(),
	features: yup.string().max(500, 'Features must be less than 500 characters').nullable(),
}).required();

const UnitForm: React.FC<UnitFormProps> = ({ unit, onSubmit, isLoading = false, error }) => {
	const {
		register,
		handleSubmit,
		control,
		watch,
		setValue,
		formState: { errors },
	} = useForm<any>({
		resolver: yupResolver(unitSchema),
		defaultValues: {
			selectedClientId: unit?.clientId || undefined,
			clientLocationId: unit?.clientLocationId || undefined,
			unitNumber: unit?.unitNumber || '',
			unitType: unit?.unitType || '',
			squareFeet: unit?.squareFeet || undefined,
			bedrooms: unit?.bedrooms || undefined,
			bathrooms: unit?.bathrooms || undefined,
			floor: unit?.floor || undefined,
			status: unit?.status ?? UnitStatus.Vacant,
			occupiedSince: unit?.occupiedSince ? unit.occupiedSince.split('T')[0] : '',
			vacantSince: unit?.vacantSince ? unit.vacantSince.split('T')[0] : '',
			monthlyRent: unit?.monthlyRent || undefined,
			securityDeposit: unit?.securityDeposit || undefined,
			accessNotes: unit?.accessNotes || '',
			features: unit?.features || '',
		}
	});

	const selectedClientId = watch('selectedClientId');

	// Fetch clients
	const { data: clientsResponse } = useQuery({
		queryKey: ['clients'],
		queryFn: () => apiService.getClients(1, 1000),
	});
	const clients = clientsResponse?.data?.data || [];

	// Fetch locations for selected client
	const { data: locationsResponse, isLoading: locationsLoading } = useQuery({
		queryKey: ['client-locations', selectedClientId],
		queryFn: () => apiService.getClientLocations(selectedClientId!),
		enabled: !!selectedClientId,
	});
	const locations = locationsResponse?.data || [];

	// Reset location when client changes
	useEffect(() => {
		if (selectedClientId && (!unit || unit.clientId !== selectedClientId)) {
			setValue('clientLocationId', undefined);
		}
	}, [selectedClientId, unit, setValue]);

	const getStatusDisplay = (status: UnitStatus): string => {
		const statusMap: Record<UnitStatus, string> = {
			[UnitStatus.Occupied]: 'Occupied',
			[UnitStatus.Vacant]: 'Vacant',
			[UnitStatus.VacantNeedsWork]: 'Vacant - Needs Work',
			[UnitStatus.UnderRenovation]: 'Under Renovation',
			[UnitStatus.OffMarket]: 'Off Market',
		};
		return statusMap[status] || 'Unknown';
	};

	const handleFormSubmit = (data: any) => {
		const submitData: CreateUnitDto | UpdateUnitDto = {
			clientLocationId: data.clientLocationId,
			unitNumber: data.unitNumber,
			unitType: data.unitType || undefined,
			squareFeet: data.squareFeet || undefined,
			bedrooms: data.bedrooms || undefined,
			bathrooms: data.bathrooms || undefined,
			floor: data.floor || undefined,
			status: data.status,
			occupiedSince: data.occupiedSince || undefined,
			vacantSince: data.vacantSince || undefined,
			monthlyRent: data.monthlyRent || undefined,
			securityDeposit: data.securityDeposit || undefined,
			accessNotes: data.accessNotes || undefined,
			features: data.features || undefined,
		};
		onSubmit(submitData);
	};

	return (
		<form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
			{error && (
				<div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
					<p className="text-red-800 dark:text-red-200">{error}</p>
				</div>
			)}

			{/* Client and Location Selection */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				{/* Client Selection */}
				<div>
					<label htmlFor="selectedClientId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
						Client *
					</label>
					<Controller
						name="selectedClientId"
						control={control}
						render={({ field }) => (
							<select
								{...field}
								id="selectedClientId"
								className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
							>
								<option value="">Select a client</option>
								{clients.map((client: any) => (
									<option key={client.id} value={client.id}>
										{client.name}
									</option>
								))}
							</select>
						)}
					/>
					{errors.selectedClientId && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.selectedClientId?.message)}</p>
					)}
				</div>

				{/* Location Selection */}
				<div>
					<label htmlFor="clientLocationId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
						Location *
					</label>
					<Controller
						name="clientLocationId"
						control={control}
						render={({ field }) => (
							<select
								{...field}
								id="clientLocationId"
								className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color disabled:opacity-50 disabled:cursor-not-allowed"
								disabled={!selectedClientId || locationsLoading}
							>
								<option value="">
									{!selectedClientId
										? 'Select a client first'
										: locationsLoading
											? 'Loading locations...'
											: locations.length === 0
												? 'No locations available'
												: 'Select a location'}
								</option>
								{locations.map((location: any) => (
									<option key={location.id} value={location.id}>
										{location.locationName}
									</option>
								))}
							</select>
						)}
					/>
					{errors.clientLocationId && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.clientLocationId?.message)}</p>
					)}
				</div>
			</div>

			{/* Unit Identification */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<div>
					<label htmlFor="unitNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
						Unit Number *
					</label>
					<input
						type="text"
						id="unitNumber"
						{...register('unitNumber')}
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
						placeholder="e.g., 101, 2B, A-205"
					/>
					{errors.unitNumber && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.unitNumber?.message)}</p>
					)}
				</div>

				<div>
					<label htmlFor="unitType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
						Unit Type
					</label>
					<input
						type="text"
						id="unitType"
						{...register('unitType')}
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
						placeholder="e.g., 1BR/1BA, 2BR/2BA, Studio"
					/>
					{errors.unitType && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.unitType?.message)}</p>
					)}
				</div>
			</div>

			{/* Unit Details */}
			<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
				<div>
					<label htmlFor="squareFeet" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
						Square Feet
					</label>
					<input
						type="number"
						id="squareFeet"
						{...register('squareFeet', { valueAsNumber: true })}
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
					/>
					{errors.squareFeet && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.squareFeet?.message)}</p>
					)}
				</div>

				<div>
					<label htmlFor="bedrooms" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
						Bedrooms
					</label>
					<input
						type="number"
						id="bedrooms"
						{...register('bedrooms', { valueAsNumber: true })}
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
					/>
					{errors.bedrooms && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.bedrooms?.message)}</p>
					)}
				</div>

				<div>
					<label htmlFor="bathrooms" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
						Bathrooms
					</label>
					<input
						type="number"
						step="0.5"
						id="bathrooms"
						{...register('bathrooms', { valueAsNumber: true })}
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
					/>
					{errors.bathrooms && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.bathrooms?.message)}</p>
					)}
				</div>

				<div>
					<label htmlFor="floor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
						Floor
					</label>
					<input
						type="number"
						id="floor"
						{...register('floor', { valueAsNumber: true })}
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
					/>
					{errors.floor && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.floor?.message)}</p>
					)}
				</div>
			</div>

			{/* Status */}
			<div>
				<label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
					Status *
				</label>
				<Controller
					name="status"
					control={control}
					render={({ field }) => (
						<select
							{...field}
							id="status"
							className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
						>
							{Object.values(UnitStatus).filter(v => typeof v === 'number').map((status) => (
								<option key={status} value={status}>
									{getStatusDisplay(status as UnitStatus)}
								</option>
							))}
						</select>
					)}
				/>
				{errors.status && (
					<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.status?.message)}</p>
				)}
			</div>

			{/* Financial Information */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<div>
					<label htmlFor="monthlyRent" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
						Monthly Rent
					</label>
					<input
						type="number"
						step="0.01"
						id="monthlyRent"
						{...register('monthlyRent', { valueAsNumber: true })}
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
					/>
					{errors.monthlyRent && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.monthlyRent?.message)}</p>
					)}
				</div>

				<div>
					<label htmlFor="securityDeposit" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
						Security Deposit
					</label>
					<input
						type="number"
						step="0.01"
						id="securityDeposit"
						{...register('securityDeposit', { valueAsNumber: true })}
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
					/>
					{errors.securityDeposit && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.securityDeposit?.message)}</p>
					)}
				</div>
			</div>

			{/* Dates */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<div>
					<label htmlFor="occupiedSince" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
						Occupied Since
					</label>
					<input
						type="date"
						id="occupiedSince"
						{...register('occupiedSince')}
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
					/>
					{errors.occupiedSince && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.occupiedSince?.message)}</p>
					)}
				</div>

				<div>
					<label htmlFor="vacantSince" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
						Vacant Since
					</label>
					<input
						type="date"
						id="vacantSince"
						{...register('vacantSince')}
						className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
					/>
					{errors.vacantSince && (
						<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.vacantSince?.message)}</p>
					)}
				</div>
			</div>

			{/* Access Notes */}
			<div>
				<label htmlFor="accessNotes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
					Access Notes
				</label>
				<textarea
					id="accessNotes"
					{...register('accessNotes')}
					rows={3}
					className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
					placeholder="Gate codes, special instructions, etc."
				/>
				{errors.accessNotes && (
					<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.accessNotes?.message)}</p>
				)}
			</div>

			{/* Features */}
			<div>
				<label htmlFor="features" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
					Features
				</label>
				<textarea
					id="features"
					{...register('features')}
					rows={3}
					className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-accent-color focus:border-accent-color"
					placeholder="e.g., Balcony, Fireplace, In-unit W/D"
				/>
				{errors.features && (
					<p className="mt-1 text-sm text-red-600 dark:text-red-400">{String(errors.features?.message)}</p>
				)}
			</div>

			{/* Submit Button */}
			<div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
				<button
					type="button"
					onClick={() => window.history.back()}
					className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
				>
					Cancel
				</button>
				<button
					type="submit"
					disabled={isLoading}
					className="px-4 py-2 bg-accent-color text-white rounded-md hover:bg-accent-color/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{isLoading ? 'Saving...' : unit ? 'Update Unit' : 'Create Unit'}
				</button>
			</div>
		</form>
	);
};

export default UnitForm;

